import { authStore } from "../../../stores/auth.store";

export const readStoredUser = () => {
  return authStore.user;
};

const getPathValue = (source, path) => {
  if (!source || !path) return undefined;
  return path.split(".").reduce((value, segment) => value?.[segment], source);
};

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return Boolean(value.trim());
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

const getFirstValue = (sources, paths) => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    for (const path of paths) {
      const value = getPathValue(source, path);
      if (hasMeaningfulValue(value)) return value;
    }
  }
  return undefined;
};

const getFirstBoolean = (sources, paths) => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    for (const path of paths) {
      const value = getPathValue(source, path);
      if (typeof value === "boolean") return value;
      if (typeof value === "number") {
        if (value === 1) return true;
        if (value === 0) return false;
      }
      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["true", "1", "yes"].includes(normalized)) return true;
        if (["false", "0", "no"].includes(normalized)) return false;
      }
    }
  }
  return undefined;
};

const toSentence = (value) => {
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) {
    return value
      .map((item) => toSentence(item))
      .filter(Boolean)
      .join(" ")
      .trim();
  }
  if (value && typeof value === "object") {
    return (
      value.message ||
      value.reason ||
      value.note ||
      value.description ||
      value.feedback ||
      ""
    )
      .toString()
      .trim();
  }
  return "";
};

export const normalizeApprovalStatus = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (
    [
      "approved",
      "active",
      "verified",
      "complete",
      "completed",
      "published",
    ].includes(normalized)
  ) {
    return "approved";
  }

  if (
    [
      "pending",
      "pending_approval",
      "pending_verification",
      "verification_pending",
      "awaiting_verification",
      "awaiting_approval",
      "awaiting_review",
      "under_review",
      "in_review",
      "submitted",
      "reviewing",
      "pending_profile_completion",
      "profile_completion_pending",
      "awaiting_profile_completion",
    ].includes(normalized)
  ) {
    return "pending";
  }

  if (
    [
      "rejected",
      "declined",
      "inactive",
      "suspended",
      "blocked",
      "needs_changes",
      "changes_requested",
      "failed_review",
      "incomplete",
      "not_approved",
      "document_incomplete",
      "incomplete_document",
      "invalid_document",
      "profile_incomplete",
      "incomplete_profile",
      "changes_needed",
      "requires_update",
    ].includes(normalized)
  ) {
    return "changes_required";
  }

  return "unknown";
};

export const resolveApprovalState = (...sources) => {
  const status = getFirstValue(sources, [
    "approvalStatus",
    "reviewStatus",
    "verificationStatus",
    "providerStatus",
    "profileStatus",
    "status",
    "state",
    "profile.status",
    "provider.status",
    "provider.approvalStatus",
    "documents.status",
    "documents.verificationStatus",
    "documents.reviewStatus",
    "kyc.status",
    "kyc.verificationStatus",
    "license.status",
    "license.verificationStatus",
    "review.status",
    "approval.status",
  ]);
  const normalizedStatus = normalizeApprovalStatus(status);
  if (normalizedStatus !== "unknown") return normalizedStatus;

  const isApproved = getFirstBoolean(sources, [
    "isApproved",
    "approved",
    "approval.approved",
    "review.approved",
    "documents.approved",
    "kyc.approved",
    "license.approved",
    "isVerified",
    "verified",
  ]);
  if (isApproved === true) return "approved";

  const needsChanges = getFirstBoolean(sources, [
    "isRejected",
    "rejected",
    "declined",
    "needsChanges",
    "requiresChanges",
    "documents.rejected",
    "kyc.rejected",
    "license.rejected",
    "review.rejected",
    "approval.rejected",
  ]);
  if (needsChanges === true) return "changes_required";

  const isPending = getFirstBoolean(sources, [
    "pendingApproval",
    "isPendingApproval",
    "inReview",
    "isInReview",
    "documents.pending",
    "kyc.pending",
    "license.pending",
    "review.pending",
    "approval.pending",
  ]);
  if (isPending === true) return "pending";

  const hasReviewFeedback = getFirstValue(sources, [
    "review.title",
    "review.message",
    "review.reason",
    "review.note",
    "review.feedback",
    "approval.title",
    "approval.message",
    "approval.reason",
    "rejectionReason",
    "reviewReason",
    "statusReason",
    "documents.reviewReason",
    "documents.validationMessage",
    "documents.reason",
    "kyc.reason",
    "license.reason",
  ]);
  if (hasMeaningfulValue(hasReviewFeedback)) return "changes_required";

  return "unknown";
};

export const resolveApprovalReason = (...sources) => {
  const title = toSentence(
    getFirstValue(sources, [
      "review.title",
      "approval.title",
      "reasonTitle",
      "reviewTitle",
      "rejectionTitle",
      "statusTitle",
    ])
  );
  const message = toSentence(
    getFirstValue(sources, [
      "review.message",
      "review.reason",
      "review.note",
      "review.feedback",
      "approval.message",
      "approval.reason",
      "approval.note",
      "rejectionReason",
      "reviewReason",
      "statusReason",
      "reason",
      "adminNote",
      "admin_note",
      "documents.reviewReason",
      "documents.validationMessage",
      "documents.reason",
      "kyc.reason",
      "kyc.reviewReason",
      "license.reason",
      "license.reviewReason",
    ])
  );

  return {
    title,
    message,
  };
};
